import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';

// Password strength utilities
import { strengthIndicator } from '../../utils/password-strength';

import { resetPassword } from '../../store/actions';
import logo from '../../assets/images/logo.jpg';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState('');
  const [strength, setStrength] = useState(0);

  const dispatch = useDispatch();

  const handlePasswordChange = (value) => {
    setPassword(value);
    setStrength(strengthIndicator(value));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Password is required');
      return;
    }

    if (strength < 4) {
      setError('Password is too weak. Try adding numbers, uppercase, or special characters.');
      return;
    }

    if (!confirmPassword) {
      setError('Confirm Password is required');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    dispatch(
      resetPassword(token, password, (res) => {
        if (res?.code === 200) {
          navigate('/login');
        } else {
          setError(res?.message || 'Something went wrong');
        }
      })
    );
  };

  return (
    <Card className="login-card">
      <CardContent className="login-card-content">

        {/* Header */}
        <Box className="login-header">
          <Box className="login-header-icon">
            <img src={logo} alt="Logo" className="admin-login-logo" />
          </Box>
          <Typography variant="h4" className="login-title">
            Admin Portal
          </Typography>
          <Typography variant="body1" className="login-subtitle">
            Create a new password
          </Typography>
        </Box>

        <Divider className="login-divider" />

        {/* Error Alert */}
        {error && (
          <Alert severity="error" className="login-error">
            {error}
          </Alert>
        )}

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit} noValidate>

          {/* New Password */}
          <TextField
            fullWidth
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => handlePasswordChange(e.target.value)}
            margin="normal"
            required
            autoComplete="new-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Confirm Password */}
          <TextField
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            autoComplete="new-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            className="login-button"
            sx={{ mt: 2 }}
          >
            Reset Password
          </Button>
        </Box>

        {/* Back to Login */}
        <Box className="login-help">
          <Typography variant="body2">
            Remember your password?{' '}
            <Button
              variant="text"
              onClick={() => navigate('/login')}
              sx={{ p: 0, textTransform: 'none' }}
            >
              Sign in
            </Button>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ResetPassword;
